import React, { useCallback, useEffect, useState } from 'react';
import {
  NoticeAction,
  NoticeChannel,
  NoticeRuleList as NoticeRuleListReturn,
  NoticeRuleReturn,
  NoticeTrigger,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Switch } from '../common/Catalyst/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage, formatMoney } from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';
import { noticeAction, noticeChannel, noticeTrigger } from './enumLabels';

interface Props {
  scope: 'hoa' | 'company';
  scopeId: string;
  canEdit: boolean;
}

const MONEY = /^\d+(\.\d{1,2})?$/;

/**
 * Where a rule sits on the timeline a homeowner experiences.
 *
 * The API orders by `offset_days` alone, so "7 days *before* due" sorts
 * between "1 day after" and "15 days after" and the list stops reading
 * as a schedule. Sorting on the signed offset fixes that;
 * `balance_threshold` rules are not on the timeline at all and go last.
 */
const timelinePosition = (r: NoticeRuleReturn): number => {
  if (r.trigger === 'balance_threshold') return Number.MAX_SAFE_INTEGER;
  return r.trigger === 'days_before_due' ? -r.offset_days : r.offset_days;
};

const whenLabel = (r: NoticeRuleReturn): string => {
  const days = `${r.offset_days} day${r.offset_days === 1 ? '' : 's'}`;
  if (r.trigger === 'days_before_due') return `${days} before due`;
  if (r.trigger === 'days_after_due') return `${days} after due`;
  return r.min_balance
    ? `balance over ${formatMoney(r.min_balance)}`
    : 'balance over a threshold';
};

/**
 * The notice schedule for an association or a management company.
 *
 * **Resolution is first-non-empty-scope-wins, and it swaps the whole
 * list.** An association inheriting four company rules that adds one of
 * its own is then running on exactly one rule, and the other three
 * silently stop. So "customise these" has to copy the inherited list
 * down first — which is what `copy-defaults` does, and why the Add
 * button is not offered until it has been.
 */
const NoticeRuleList: React.FC<Props> = ({ scope, scopeId, canEdit }) => {
  const [data, setData] = useState<NoticeRuleListReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const [editing, setEditing] = useState<NoticeRuleReturn | null>(null);
  const [adding, setAdding] = useState(false);
  const [trigger, setTrigger] = useState<NoticeTrigger>('days_after_due');
  const [offsetDays, setOffsetDays] = useState('7');
  const [minBalance, setMinBalance] = useState('');
  const [action, setAction] = useState<NoticeAction>('send');
  const [channel, setChannel] = useState<NoticeChannel>('email');
  const [templateKey, setTemplateKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: list } =
        scope === 'hoa'
          ? await billingService.listHoaNoticeRules({ hoaId: scopeId })
          : await billingService.listCompanyNoticeRules({
              companyId: scopeId,
            });
      setData(list);
    } catch (err) {
      console.error('Error loading notice rules:', err);
      setAlert({
        type: 'error',
        title: "Couldn't load the notice schedule",
        message: billingErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = (): void => {
    setEditing(null);
    setTrigger('days_after_due');
    setOffsetDays('7');
    setMinBalance('');
    setAction('send');
    setChannel('email');
    setTemplateKey('');
    setEnabled(true);
    setErrors({});
    setAdding(true);
  };

  const openEdit = (r: NoticeRuleReturn): void => {
    setEditing(r);
    setTrigger(r.trigger);
    setOffsetDays(String(r.offset_days));
    setMinBalance(r.min_balance ?? '');
    setAction(r.action);
    setChannel(r.channel);
    setTemplateKey(r.template_key);
    setEnabled(r.enabled);
    setErrors({});
    setAdding(true);
  };

  const copyDefaults = async (): Promise<void> => {
    try {
      setBusy(true);
      await billingService.copyHoaNoticeRules({ hoaId: scopeId });
      setAlert({
        type: 'success',
        title: 'The schedule is now this association’s own',
        message:
          'Every inherited rule was copied down, so editing one cannot ' +
          'silently stop the others.',
      });
      await load();
    } catch (err) {
      console.error('Error copying notice rules:', err);
      setAlert({
        type: 'error',
        title: "Couldn't copy the schedule",
        message: billingErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!templateKey.trim()) {
      found.templateKey = 'Which notice should go out?';
    }
    if (trigger !== 'balance_threshold' && !/^\d+$/.test(offsetDays.trim())) {
      found.offsetDays = 'Enter a whole number of days.';
    }
    if (trigger === 'balance_threshold' && !MONEY.test(minBalance.trim())) {
      // The backend refuses this too — a threshold rule with no
      // threshold fires on every home that owes a cent.
      found.minBalance = 'A balance rule needs a threshold.';
    }
    if (minBalance.trim() && !MONEY.test(minBalance.trim())) {
      found.minBalance = 'Enter an amount like 100 or 100.00.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const body = {
      trigger,
      offset_days: trigger === 'balance_threshold' ? 0 : Number(offsetDays),
      min_balance: minBalance.trim() || null,
      action,
      channel,
      template_key: templateKey.trim(),
      enabled,
    };

    try {
      setBusy(true);
      if (editing) {
        await billingService.updateNoticeRule({
          ruleId: editing.id,
          noticeRuleUpdate: body,
        });
      } else if (scope === 'hoa') {
        await billingService.createHoaNoticeRule({
          hoaId: scopeId,
          noticeRuleCreate: body,
        });
      } else {
        await billingService.createCompanyNoticeRule({
          companyId: scopeId,
          noticeRuleCreate: body,
        });
      }
      setAdding(false);
      await load();
    } catch (err) {
      console.error('Error saving notice rule:', err);
      setErrors({ form: billingErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: NoticeRuleReturn): Promise<void> => {
    try {
      setBusy(true);
      await billingService.deleteNoticeRule({ ruleId: r.id });
      await load();
    } catch (err) {
      console.error('Error deleting notice rule:', err);
      setAlert({
        type: 'error',
        title: "Couldn't delete that rule",
        message: billingErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const rules = [...data.rules].sort(
    (a, b) => timelinePosition(a) - timelinePosition(b)
  );
  const isOwn = !data.inherited;

  return (
    <>
      {alert && (
        <div className="mb-4">
          <ShowAlert
            alertType={alert.type}
            alertTitle={alert.title}
            alertMessage={alert.message}
            isVisible={!!alert}
            onClose={() => setAlert(null)}
            timeout={alert.type === 'success' ? 8000 : 14000}
            isInline
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Notice schedule</Subheading>
          <Text className="mt-1">
            When a homeowner hears about an unpaid charge, and whether it goes
            out by itself or waits for a person.
          </Text>
        </div>
        {canEdit && isOwn && (
          <Button color="sky" onClick={openAdd}>
            Add a rule
          </Button>
        )}
      </div>

      {data.inherited && (
        <div className="mb-4 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-4">
          <Subheading>
            These are the{' '}
            {data.effective_scope_type === 'company'
              ? 'management company’s'
              : 'platform’s'}{' '}
            rules, not this association’s
          </Subheading>
          <Text className="mt-1">
            This association has no schedule of its own, so it runs on the
            inherited one.{' '}
            <strong>
              Adding a single rule here would replace the whole list
            </strong>{' '}
            — resolution takes the first scope that has any rules at all. Copy
            them down first and the rest keep working.
          </Text>
          {canEdit && scope === 'hoa' && (
            <Button
              color="sky"
              className="mt-3"
              onClick={copyDefaults}
              disabled={busy}
            >
              {busy ? 'Copying…' : 'Copy these down to customise them'}
            </Button>
          )}
        </div>
      )}

      {rules.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            No notice rules anywhere, so no reminders go out at all. That is the
            safe default rather than an error — nothing is sent because somebody
            forgot to configure it.
          </Text>
        </div>
      ) : (
        <Table dense>
          <TableHead>
            <TableRow>
              <TableHeader>When</TableHeader>
              <TableHeader>Notice</TableHeader>
              <TableHeader>How</TableHeader>
              <TableHeader>Sent</TableHeader>
              <TableHeader>Only over</TableHeader>
              {canEdit && isOwn && <TableHeader> </TableHeader>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{whenLabel(r)}</TableCell>
                <TableCell>{r.template_key}</TableCell>
                <TableCell>{noticeChannel.label(r.channel)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge color={noticeAction.color(r.action)}>
                      {noticeAction.label(r.action)}
                    </Badge>
                    {!r.enabled && <Badge color="zinc">Off</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  {r.min_balance ? formatMoney(r.min_balance) : '—'}
                </TableCell>
                {canEdit && isOwn && (
                  <TableCell>
                    <Button plain onClick={() => openEdit(r)}>
                      Edit
                    </Button>
                    <Button plain onClick={() => remove(r)} disabled={busy}>
                      Delete
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {isOwn && rules.length === 1 && (
        <Text className="mt-3">
          One rule is the whole schedule for this scope. Deleting it hands this
          association back to the rules it inherits rather than sending nothing.
        </Text>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} size="2xl">
        <DialogTitle>
          {editing ? 'Edit notice rule' : 'Add a notice rule'}
        </DialogTitle>
        <DialogBody>
          {errors.form && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="That rule was not saved"
                alertMessage={errors.form}
                isVisible
                onClose={() => setErrors((e) => ({ ...e, form: '' }))}
                timeout={0}
                isInline
              />
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Trigger</Label>
                <FieldPicker
                  label="Trigger"
                  value={trigger}
                  onChange={(v) => setTrigger(v as NoticeTrigger)}
                  options={noticeTrigger.options().map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                {noticeTrigger.help(trigger) && (
                  <Description className="mt-1">
                    {noticeTrigger.help(trigger)}
                  </Description>
                )}
              </Field>

              {trigger !== 'balance_threshold' && (
                <TextSetting
                  label="Days"
                  suffix="days"
                  value={offsetDays}
                  error={errors.offsetDays}
                  onChange={setOffsetDays}
                />
              )}

              <TextSetting
                label="Only when the balance is over"
                help={
                  trigger === 'balance_threshold'
                    ? 'Required. Without it this fires on every home that owes a cent.'
                    : 'Optional — skips trivial balances.'
                }
                prefix="$"
                placeholder={
                  trigger === 'balance_threshold' ? 'Required' : 'Any balance'
                }
                value={minBalance}
                error={errors.minBalance}
                onChange={setMinBalance}
              />

              <Field>
                <Label>Notice</Label>
                <Description>
                  Which template goes out — e.g. <code>dues_reminder</code>,{' '}
                  <code>dues_demand</code>.
                </Description>
                <Input
                  value={templateKey}
                  invalid={!!errors.templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                />
                {errors.templateKey && (
                  <ErrorMessage>{errors.templateKey}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Send or flag</Label>
                <Description>
                  Per rule, deliberately: an association can send the friendly
                  reminder by itself and still require a person to release a
                  demand letter.
                </Description>
                <FieldPicker
                  label="Send or flag"
                  value={action}
                  onChange={(v) => setAction(v as NoticeAction)}
                  options={noticeAction.options().map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                {noticeAction.help(action) && (
                  <Description className="mt-1">
                    {noticeAction.help(action)}
                  </Description>
                )}
              </Field>

              <Field>
                <Label>Channel</Label>
                <FieldPicker
                  label="Channel"
                  value={channel}
                  onChange={(v) => setChannel(v as NoticeChannel)}
                  options={noticeChannel.options().map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </Field>

              <Field className="flex items-start justify-between gap-4">
                <div>
                  <Label>Enabled</Label>
                  <Description>
                    Off keeps the rule but stops it firing.
                  </Description>
                </div>
                <Switch
                  checked={enabled}
                  onChange={setEnabled}
                  aria-label="Enabled"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setAdding(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="sky" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save rule' : 'Add rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoticeRuleList;
